// Copyright ©️ 2026 eVolve MEP, LLC

import type { ShopRole } from '@evolvemep/api-ts';

import { strongEntries } from 'helpers/strongEntries';
import { type AccessArea, getRolesWithAccess } from 'hooks/user/useHasAccess';

import type { RoleOption } from './types';

const accessAreaToPermissionName = {
  insights: 'Shop Insights',
  modelBrowser: 'Model Browser',
  workOrders: 'Work Orders',
  tasks: 'Tasks',
  facilities: 'Facilities',
  projects: 'Projects',
  catalog: 'Catalog',
} as const satisfies Partial<Record<AccessArea, string>>;

const shopSpecificRoles = ['Shop.ShopFloor', 'Shop.FullAccess', 'Shop.LimitedAccess'] as const satisfies ShopRole[];

type ShopPermissionsRowData = {
  'permissionLabel': string;
  'Company.Administrator': RoleOption;
  'Shop.ShopFloor': RoleOption;
  'Shop.FullAccess': RoleOption;
  'Shop.LimitedAccess': RoleOption;
};

export const shopPermissionsData = strongEntries(accessAreaToPermissionName).map(
  ([accessArea, permissionLabel]): ShopPermissionsRowData => {
    // Since work orders have their own state & action dependent truth table,
    // we want to show a checkmark even if the user only has "read" access.
    const accessRequired = accessArea === 'workOrders' ? 'read' : 'write';
    const rolesWithAccess = getRolesWithAccess(accessArea, accessRequired);
    const shopRoleAccess = Object.fromEntries(
      shopSpecificRoles.map((role) => [role, { assigned: rolesWithAccess.includes(role) }]),
    ) as Record<(typeof shopSpecificRoles)[number], RoleOption>;
    return { permissionLabel, 'Company.Administrator': { assigned: true }, ...shopRoleAccess };
  },
);
